import React, { useEffect, useState ,useRef} from 'react';
import axios from 'axios';
import LogoHeader from '../components/LogoHeader';
import {jwtDecode } from "jwt-decode";
import TimeSelection from '../components/steps/TimeSelection';
import PaymentForm from '../components/steps/PaymentForm';
import OtpVerification from '../components/steps/OtpVerification';
import PaymentSuccess from '../components/steps/PaymentSuccess';
import PaymentFailure from '../components/steps/PaymentFailure';
import PortSelection from '../components/steps/PortSelection';
import ChargingTimer from '../components/steps/ChargingTimer';
import { useNavigate, useLocation } from 'react-router-dom';
import { getMobileRedirectPath } from '../utils/redirect';
import { API_ENDPOINTS } from '../config/constants';

const Home = () => {
    const [step, setStep] = useState(() => {
        const savedStep = localStorage.getItem('currentStep');
        return savedStep ? parseInt(savedStep) : 1; 
    });
    const [portData, setPortData] = useState({
        id: null,
        port_number: null,
    });
    const [timeData, setTimeData] = useState({
        time: 0,
        price: []
    });
    const [paymentData, setPaymentData] = useState(null);
    const [orderId, setOrderId] = useState(null);
    const [otpStatus, setOtpStatus] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [totalTime, setTotalTime] = useState(() => {
        const saved = localStorage.getItem('chargingTotalSeconds');
        return saved ? parseInt(saved, 10) : 0;
    });
    // Absolute epoch ms the backend says charging ends at — the single source
    // of truth the countdown is derived from, instead of a locally ticked counter.
    const [endTime, setEndTime] = useState(() => {
        const saved = localStorage.getItem('chargingEndTime');
        return saved ? parseInt(saved, 10) : null;
    });
    const navigate = useNavigate();
    const location = useLocation();
    const timerRef = useRef(null);


    useEffect(() => {
    const token = localStorage.getItem("token");

    if (isTokenExpired(token)) {
    localStorage.clear();
    navigate('/mobile');
    }
  }, []);

    useEffect(() => {
        if(otpStatus === 'success' && secondsLeft > 0) {
        localStorage.setItem('currentStep', step.toString());
        }
    }, [step]);

    // Recomputes secondsLeft from the absolute endTime on every tick, so drift
    // from backgrounded tabs or timer throttling self-corrects instead of
    // accumulating like a naive decrement-by-one-per-tick would.
    const startTimer = () => {
        if (timerRef.current) return; // Prevent multiple intervals

        timerRef.current = setInterval(() => {
            setEndTime(currentEndTime => {
                if (!currentEndTime) return currentEndTime;
                const remaining = Math.max(0, Math.round((currentEndTime - Date.now()) / 1000));
                setSecondsLeft(remaining);
                if (remaining <= 0) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
                return currentEndTime;
            });
        }, 1000);
    };

    // On mount (including a browser refresh, which remounts this page), resume
    // the countdown from whatever endTime is in localStorage, then reconcile
    // against the backend so the clock reflects the actual scheduled stop time.
    useEffect(() => {
        if (step !== 6) return;

        if (endTime) {
            setSecondsLeft(Math.max(0, Math.round((endTime - Date.now()) / 1000)));
            startTimer();
        }

        const txId = localStorage.getItem('chargingTransactionId');
        if (txId) {
            const token = localStorage.getItem('token');
            axios.get(`${API_ENDPOINTS.TRANSACTION}/${txId}/status`, {
                headers: { Authorization: `Bearer ${token}` },
            }).then(({ data }) => {
                if (!data?.end_time) return;
                const endTimeMs = new Date(data.end_time).getTime();
                const durationSeconds = data.duration_minutes
                    ? data.duration_minutes * 60
                    : totalTime;
                localStorage.setItem('chargingEndTime', endTimeMs.toString());
                localStorage.setItem('chargingTotalSeconds', durationSeconds.toString());
                setEndTime(endTimeMs);
                setTotalTime(durationSeconds);
                setSecondsLeft(Math.max(0, Math.round((endTimeMs - Date.now()) / 1000)));
                startTimer();
            }).catch((err) => {
                console.error('Failed to sync charging session with backend:', err);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    const goNext = () => setStep((prev) => prev + 1);
    const goBack = () => setStep((prev) => prev - 1);
    const goToStep = (stepNum) => setStep(stepNum);

    function isTokenExpired(token) {
        if (!token) return true;

        try {
            const decoded = jwtDecode(token);
            const now = Date.now() / 1000; // in seconds
            return decoded.exp < now;  // true if expired
        } catch (e) {
            // Invalid token
            return true;
        }
        }

    const renderStep = () => {
        switch (step) {
            case 1:
                return <TimeSelection goBackCondn={(step)=>goToStep(step)} portData={portData} secondsLeft={secondsLeft} setOrderId={(id)=>setOrderId(id)}  onNext={(data) => {setTimeData((prev) => (prev ?{
                                                            time: (data.time ),         
                                                            price: (data.price)
                                                                }:data));
                                                                if (secondsLeft > 0) {
                                                                    goToStep(3)
                                                                }else {
                                                                    goNext();
                                                                }
                                                            }} />;
            case 2:
                return <PortSelection onNext={goNext} setPortData={setPortData} setOrderId={(id)=>setOrderId(id)} />;
            case 3:
                return <PaymentForm timeData={timeData} orderId={orderId} portData={portData} onVerify={(status) => {setOtpStatus(status)}} onNext={(data) => {
                    setPaymentData(data);
                    if (data?.end_time) {
                        const endTimeMs = new Date(data.end_time).getTime();
                        const durationSeconds = data.duration_minutes
                            ? data.duration_minutes * 60
                            : timeData.time * 60;
                        localStorage.setItem('chargingEndTime', endTimeMs.toString());
                        localStorage.setItem('chargingTotalSeconds', durationSeconds.toString());
                        if (data.transaction_id) {
                            localStorage.setItem('chargingTransactionId', data.transaction_id);
                        }
                        setEndTime(endTimeMs);
                        setTotalTime(durationSeconds);
                        setSecondsLeft(Math.max(0, Math.round((endTimeMs - Date.now()) / 1000)));
                    }
                    goNext();
                    startTimer();
                }} onBack={()=>{
                    if (secondsLeft > 0) {
                        goToStep(1);
                    }else {
                        goBack();
                    }
                }} />;
            // case 4:
            //     return <OtpVerification 
            //         if (status === 'success') goNext();
            //         else setStep(6);
            //     }} onBack={goBack} />;
            case 4:
                return <PaymentSuccess onNext={() => {
                                                        goNext();
                                                        }}/>;
            case 5:
                return <PaymentFailure onRetry={() => goToStep(3)} />;
            case 6:
                return <ChargingTimer secondsLeft={secondsLeft} totalTime={totalTime} setSecondsLeft={setSecondsLeft} onAddTime={() => {goToStep(1); setOtpStatus(null);}} onDone={() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('currentStep');
                    localStorage.removeItem('chargingEndTime');
                    localStorage.removeItem('chargingTotalSeconds');
                    localStorage.removeItem('chargingTransactionId');
                    setOtpStatus(null);
                    navigate(getMobileRedirectPath(location.search));
                }} />;
            default:
                return <p>Invalid Step</p>;
        }
    };

    return (
        <div style={{ minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: "space-around" }}>
            <LogoHeader />
            <div className="card" style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {renderStep()}
            </div>
            <footer style={{ padding: '1rem', textAlign: 'center', color: '#aaa' }}>
                Copyright © SR4Dx
            </footer>
        </div>
    );
};

export default Home;
